import { Component } from 'react';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { connect } from 'react-redux';

import Button from 'components/atoms/Button';
import Form from 'components/atoms/Form';
import Icon from 'components/atoms/Icon';
import ToggleButton from 'components/atoms/ToggleButton';
import { PlanType, PortAreaType, RootState } from 'types';
import { MapControlsInterface } from 'types/functions';
import { ActionsType } from 'types/system';
import { getId, getMode } from 'utils/formHandlers';
import { translate } from 'utils/translate';

interface PlanFeatureModelProps {
    children?: string | JSX.Element | JSX.Element[];
    title?: string;
    language: string;
    id?: string;
    data: any;
    mapControls: MapControlsInterface;
    actions: ActionsType | undefined;
    pilotagePlans: PlanType[];
    mode: string;
    portAreas: PortAreaType[];
}

interface PlanFeatureModelState {
    language: string;
    errors: object;
    data: any | null;
    pilotagePlans: PlanType[];
    pilotagePlan: PlanType | null;
    pilotagePlanSetting: {
        type: string;
        key: string;
        name: string;
        disabled: boolean;
        value: false;
        options: { key: string; name: string; value: boolean; label: string }[];
    }[];
}

class PlanFeatureModel extends Component<PlanFeatureModelProps, PlanFeatureModelState> {
    constructor(props: PlanFeatureModelProps) {
        super(props);
        this.state = {
            pilotagePlans: [],
            data: null,
            pilotagePlan: null,
            errors: {},
            language: 'en',
            pilotagePlanSetting: [],
        };
    }

    componentDidMount(): void {
        this.setState({
            pilotagePlanSetting: this.props.data.pilotagePlanSetting
                .map((setting) => {
                    const mode = getMode(this.props.mode);
                    return {
                        ...setting,
                        options: setting.options
                            .filter((option) => {
                                return option !== '' && option;
                            })
                            .map((option) => {
                                // eslint-disable-next-line no-unsafe-optional-chaining
                                const { visible, name } = this.props.mapControls[mode]?.portAreaFeatureVisiblity(
                                    option,
                                    this.getType(setting.type)
                                );

                                return {
                                    key: getId(),
                                    name: option,
                                    value: visible,
                                    label: name,
                                };
                            }),
                    };
                })
                .map((setting) => {
                    return {
                        ...setting,
                        value: setting.options.every((option) => option.value),
                    };
                }),
        });
    }

    getType = (type) => {
        switch (type) {
            case 'reservedAreas':
                return [type, 'noGoAreas'];

            case 'wayPoint':
                return [type, 'markerPoints'];

            default:
                return type;
        }
    };

    handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e?.preventDefault();
        this.props.actions?.onCancel();
    };

    handleFormCancel = () => {
        this.props.actions?.onCancel();
    };

    updateVisiblity = (groupKey: string, optionkey: string[], visiblity: boolean) => {
        const areaNames: string[] = [];
        let areatype = '';
        this.setState(
            {
                //@ts-ignore
                pilotagePlanSetting: this.state.pilotagePlanSetting
                    .map((setting) => {
                        if (setting.key !== groupKey) {
                            return setting;
                        }
                        return {
                            ...setting,
                            options: setting.options.map((option) => {
                                if (!optionkey.includes(option.key)) {
                                    return option;
                                }
                                areatype = setting.type;
                                if (option.name !== '' && option.name) {
                                    areaNames.push(option.name);
                                }

                                return {
                                    ...option,
                                    value: visiblity,
                                };
                            }),
                        };
                    })
                    .map((setting) => {
                        return {
                            ...setting,
                            value: setting.options.every((option) => option.value),
                        };
                    }),
            },
            () => {
                if (this.props.mode === 'playback') {
                    this.props.mapControls.playback?.portAreaFeatureVisiblity(
                        areaNames,
                        this.getType(areatype),
                        visiblity,
                        true
                    );
                } else if (this.props.mode === 'time' || this.props.mode === 'live') {
                    this.props.mapControls.time?.portAreaFeatureVisiblity(
                        areaNames,
                        this.getType(areatype),
                        visiblity,
                        true
                    );
                } else {
                    this.props.mapControls.chart?.portAreaFeatureVisiblity(
                        areaNames,
                        this.getType(areatype),
                        visiblity,
                        true
                    );
                }
            }
        );
    };

    getAreaName = (key) => {
        const area = this.props.portAreas?.find((x) => {
            return x.key === key;
        });
        if (area) {
            return area.name;
        }

        return key;
    };

    render() {
        return (
            <>
                <Form onSubmit={this.handleFormSubmit} className="bg-white shadow w-mediumModel  rounded ">
                    <div className="p-4 no-select">
                        <div id={`Drag-${this.props.id}`} className="flex justify-between mb-2 ">
                            <div className="text-lg leading-6 font-bold">{this.props.data.plan?.name} </div>
                            <div
                                onTouchEnd={this.handleFormCancel}
                                onClick={this.handleFormCancel}
                                className="cursor-pointer"
                            >
                                <Icon>
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                    <path d="M18 6l-12 12" />
                                    <path d="M6 6l12 12" />
                                </Icon>
                            </div>
                        </div>
                        <div>
                            <Scrollbars style={{ maxHeight: 400, minHeight: 250 }}>
                                {this.state.pilotagePlanSetting
                                    .filter((setting) => {
                                        return !setting.disabled;
                                    })
                                    .map((setting) => {
                                        return (
                                            <div key={setting.key} className="flex flex-col  gap-1 py-1">
                                                <div className="font-bold">{translate(setting.name)} </div>
                                                {setting.disabled || setting.options.length === 0 ? (
                                                    <div className="pl-2">{`${translate('No')} ${translate(
                                                        setting.name
                                                    )}`}</div>
                                                ) : (
                                                    <div>
                                                        <div className="flex flex-col pl-2">
                                                            {setting.type === 'wayPoint' && (
                                                                <div className="flex">
                                                                    <div className="flex items-center ">
                                                                        <ToggleButton
                                                                            disabled={setting.disabled}
                                                                            onChange={() => {
                                                                                this.updateVisiblity(
                                                                                    setting.key,
                                                                                    setting.options.map((x) => {
                                                                                        return x.key;
                                                                                    }),
                                                                                    !setting.value
                                                                                );
                                                                            }}
                                                                            value={setting.value}
                                                                        />
                                                                    </div>
                                                                    <div className="pl-2">
                                                                        {translate('All points')}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {setting.options
                                                                .filter((option) => {
                                                                    return option.name != '';
                                                                })
                                                                .map((option) => {
                                                                    return (
                                                                        <div key={option.key} className="flex">
                                                                            <div className="flex items-center ">
                                                                                <ToggleButton
                                                                                    disabled={setting.disabled}
                                                                                    onChange={() => {
                                                                                        this.updateVisiblity(
                                                                                            setting.key,
                                                                                            [option.key],
                                                                                            !option.value
                                                                                        );
                                                                                    }}
                                                                                    value={option.value}
                                                                                />
                                                                            </div>
                                                                            <div className="pl-2">{option.label}</div>
                                                                        </div>
                                                                    );
                                                                })}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                            </Scrollbars>
                        </div>
                    </div>
                    <div className="flex justify-end p-4 hidden">
                        <Button className="px-4 py-1 font-bold border border-gray-200" onClick={this.handleFormCancel}>
                            {translate('No , Cancel')}
                        </Button>
                        <Button type="submit" className="px-4 py-2 bg-primary-submit rounded-md text-white">
                            {translate('Yes , Save')}
                        </Button>
                    </div>
                </Form>
            </>
        );
    }
}

const planFeatureModelStateToProps = (state: RootState) => ({
    mapControls: state.map.mapControls,
    language: state.user.user.language,
    pilotagePlans: state.port.pilotagePlans,
    portAreas: state.port.port?.port_areas,
    mode: state.layout.mode,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const planFeatureModelDispatchToProps = () => ({});

export default connect(planFeatureModelStateToProps, planFeatureModelDispatchToProps)(PlanFeatureModel);
